import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { dispatch } from '../../utils/event-helpers';
import { generateUid } from '../../utils/uid';
import { styles } from './asc-checkbox.styles';

export type CheckboxType = 'checkbox' | 'radio';
export type CheckboxSize = 'sm' | 'md';

/**
 * A styled checkbox or radio button control with a required label and optional supporting text.
 *
 * Presentational only — no form association or `ElementInternals`. Consumers manage
 * `checked` state in response to the `asc-change` event. Both the checkbox indicator
 * (rounded rect) and radio button (circle) are driven by the `type` property, making
 * this a single component that covers both form control primitives.
 *
 * @element asc-checkbox
 *
 * @fires asc-change - Fired when the user activates the control (not on programmatic changes).
 *   Detail: `{ checked: boolean, indeterminate: boolean, value: string }`
 *
 * @slot - **Required.** The label text rendered to the right of the indicator.
 * @slot supporting-text - Optional secondary description rendered below the label.
 *
 * @csspart root            - The wrapping `<label>` element (flex row wrapper).
 * @csspart input           - The visually hidden native `<input>` (provides AT semantics).
 * @csspart control         - The custom-styled indicator box or circle.
 * @csspart check-icon      - The `<svg>` checkmark icon (checkbox checked, not indeterminate).
 * @csspart dash-icon       - The `<svg>` dash icon (checkbox indeterminate).
 * @csspart dot             - The inner dot `<div>` (radio checked).
 * @csspart label-group     - The `<div>` wrapping label and supporting-text spans.
 * @csspart label           - The `<span>` wrapping the default label slot.
 * @csspart supporting-text - The `<span>` wrapping the supporting-text slot.
 *
 * @cssprop --asc-checkbox-size-sm                       - Indicator size at `size="sm"`. Default: `16px`
 * @cssprop --asc-checkbox-size-md                       - Indicator size at `size="md"`. Default: `20px`
 * @cssprop --asc-checkbox-radius-sm                     - Checkbox border-radius at sm. Default: `var(--asc-radius-xs)`
 * @cssprop --asc-checkbox-radius-md                     - Checkbox border-radius at md. Default: `var(--asc-radius-sm)`
 * @cssprop --asc-checkbox-radio-radius                  - Radio border-radius. Default: `var(--asc-radius-full)`
 * @cssprop --asc-checkbox-border-color                  - Unchecked indicator border. Default: `var(--asc-border-primary)`
 * @cssprop --asc-checkbox-checked-bg                    - Checked/indeterminate fill. Default: `var(--asc-bg-brand-solid)`
 * @cssprop --asc-checkbox-checked-bg-hover              - Checked fill on hover. Default: `var(--asc-bg-brand-solid_hover)`
 * @cssprop --asc-checkbox-icon-color                    - Check, dash, or dot color. Default: `var(--asc-fg-white)`
 * @cssprop --asc-checkbox-disabled-bg                   - Disabled indicator background. Default: `var(--asc-bg-disabled_subtle)`
 * @cssprop --asc-checkbox-disabled-border               - Disabled indicator border. Default: `var(--asc-border-disabled)`
 * @cssprop --asc-checkbox-disabled-icon-color           - Disabled inner icon/dot color. Default: `var(--asc-fg-disabled_subtle)`
 * @cssprop --asc-checkbox-disabled-label-color          - Disabled label and supporting text color. Default: `var(--asc-fg-disabled)`
 * @cssprop --asc-checkbox-label-color                   - Label text color. Default: `var(--asc-text-secondary)`
 * @cssprop --asc-checkbox-label-font-size-sm            - Label font size at `size="sm"`. Default: `var(--asc-font-size-text-sm)`
 * @cssprop --asc-checkbox-label-font-size-md            - Label font size at `size="md"`. Default: `var(--asc-font-size-text-md)`
 * @cssprop --asc-checkbox-label-font-weight             - Label font weight. Default: `var(--asc-font-weight-medium)`
 * @cssprop --asc-checkbox-supporting-text-color         - Supporting text color. Default: `var(--asc-text-tertiary)`
 * @cssprop --asc-checkbox-supporting-text-font-size-sm  - Supporting text size at sm. Default: `var(--asc-font-size-text-sm)`
 * @cssprop --asc-checkbox-supporting-text-font-size-md  - Supporting text size at md. Default: `var(--asc-font-size-text-md)`
 * @cssprop --asc-checkbox-supporting-text-font-weight   - Supporting text font weight. Default: `var(--asc-font-weight-regular)`
 * @cssprop --asc-checkbox-focus-ring-gap-color          - Inner 2px focus ring gap. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-checkbox-focus-ring-color              - Outer 4px focus ring. Default: `var(--asc-focus-ring)`
 * @cssprop --asc-checkbox-gap                           - Gap between indicator and label group. Default: `var(--asc-spacing-sm)`
 */
@customElement('asc-checkbox')
export class AscCheckbox extends LitElement {
  static override styles = styles;

  private readonly _uid = generateUid();

  /** The control type — `"checkbox"` renders a rounded-rect indicator; `"radio"` renders a circle. Both types toggle `checked` on each click. */
  @property({ type: String, reflect: true }) type: CheckboxType = 'checkbox';

  /** Visual size of the indicator and label typography. */
  @property({ type: String, reflect: true }) size: CheckboxSize = 'sm';

  /** Whether the control is in the selected/on state. */
  @property({ type: Boolean, reflect: true }) checked = false;

  /**
   * Checkbox only. Renders the dash/minus icon and overrides the checked visual.
   * Cleared to `false` when the user activates the control.
   * Has no effect when `type="radio"`.
   */
  @property({ type: Boolean, reflect: true }) indeterminate = false;

  /** Prevents interaction and applies the disabled visual treatment. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Consumer-managed value string associated with this control. */
  @property({ type: String, reflect: true }) value = '';

  @query('[part="input"]') private _input!: HTMLInputElement;

  @state() private _hasSupportingText = false;

  override updated(changed: PropertyValues<this>): void {
    if (changed.has('disabled')) {
      if (this.disabled) {
        this.setAttribute('aria-disabled', 'true');
      } else {
        this.removeAttribute('aria-disabled');
      }
    }

    // `indeterminate` cannot be set via HTML attribute — must be set as a JS property.
    if (changed.has('indeterminate') || changed.has('type')) {
      if (this._input) {
        this._input.indeterminate = this.type === 'checkbox' && this.indeterminate;
      }
    }
  }

  private _handleChange(e: Event): void {
    e.stopPropagation(); // prevent the native change from leaking out of shadow root
    if (this.disabled) return;

    if (this.type === 'radio') {
      // The native `change` event for radio only fires when transitioning unchecked → checked.
      // The checked → unchecked case is handled by _handleClick below.
      this.checked = true;
    } else {
      if (this.indeterminate) {
        this.checked = true;
        this.indeterminate = false;
      } else {
        this.checked = !this.checked;
      }
    }

    dispatch<{ checked: boolean; indeterminate: boolean; value: string }>(this, 'asc-change', {
      detail: { checked: this.checked, indeterminate: this.indeterminate, value: this.value },
    });
  }

  private _handleClick(e: MouseEvent): void {
    // Handle the one case the native `change` event misses: clicking an already-checked radio.
    // For all other interactions the native change event fires and _handleChange handles it.
    if (this.disabled) return;
    if (this.type === 'radio' && this.checked) {
      e.preventDefault(); // stop the label from re-activating the already-checked native input
      this.checked = false;
      dispatch<{ checked: boolean; indeterminate: boolean; value: string }>(this, 'asc-change', {
        detail: { checked: this.checked, indeterminate: this.indeterminate, value: this.value },
      });
    }
  }

  private _onSupportingTextSlotChange(e: Event): void {
    this._hasSupportingText =
      (e.target as HTMLSlotElement).assignedNodes({ flatten: true }).length > 0;
  }

  override render() {
    const supportingId = `${this._uid}-supporting`;

    return html`
      <label part="root" @click=${this._handleClick}>
        <input
          part="input"
          type=${this.type}
          .checked=${this.checked}
          ?disabled=${this.disabled}
          aria-describedby=${this._hasSupportingText ? supportingId : nothing}
          @change=${this._handleChange}
        />
        <div part="control" aria-hidden="true">
          <svg
            part="check-icon"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M1.5 6.5L4.5 9.5L10.5 2.5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <svg
            part="dash-icon"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M2 6H10"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
          <div part="dot"></div>
        </div>
        <div part="label-group">
          <span part="label"><slot></slot></span>
          <span
            part="supporting-text"
            id=${supportingId}
            ?hidden=${!this._hasSupportingText}
          >
            <slot
              name="supporting-text"
              @slotchange=${this._onSupportingTextSlotChange}
            ></slot>
          </span>
        </div>
      </label>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-checkbox': AscCheckbox;
  }
}
